import { useEffect, useMemo, useState } from 'react'
import { Lock } from 'lucide-react'
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Input,
  Textarea,
} from '@/components/ui'
import { QueryBoundary } from '@/components/domain'
import { useT } from '@/internationalization/i18n'
import { usePermissionCatalogue } from '../hooks'
import { PERMISSIONS, type PermissionDescriptor, type RoleSummary } from '../permissions'
import { usePermissions } from '../use-permissions'

const CATEGORY_KEY: Record<string, string> = {
  meetings: 'roles.category.meetings',
  actions: 'roles.category.actions',
  insight: 'roles.category.insight',
  patrons: 'roles.category.patrons',
  workspace: 'roles.category.workspace',
  billing: 'roles.category.billing',
}

/**
 * What the two meeting permissions add up to, stated before the save lands.
 *
 * ONE AXIS WITH THREE STATES, not two independent warnings. An earlier version
 * showed one alert per missing permission, so a blank new role displayed "No
 * meeting access at all" AND "Own meetings only" stacked together — which
 * contradict each other and made the form look broken before anything had been
 * ticked.
 *
 * The "no access" case is the one that surprises people: a role without
 * `meeting:read` sees nothing at all, not even the meetings its holders were
 * personally invited to. That is a legitimate configuration for a billing-only
 * role and a shock if you meant "just hide other people's".
 */
function meetingAccessNotice(selected: Set<string>) {
  const own = selected.has(PERMISSIONS.meetingRead)
  const all = selected.has(PERMISSIONS.meetingReadAll)
  if (all) return null // The permissive case needs no warning.
  if (own) {
    return {
      titleKey: 'roles.editor.ownMeetingsOnlyTitle',
      bodyKey: 'roles.editor.ownMeetingsOnlyBody',
    }
  }
  return {
    titleKey: 'roles.editor.noAccessTitle',
    bodyKey: 'roles.editor.noAccessBody',
  }
}

export interface RoleEditorProps {
  role: RoleSummary | null
  /** True when creating rather than editing. */
  isNew: boolean
  isOpen: boolean
  isSaving: boolean
  onClose: () => void
  onSave: (body: { name: string; description: string | null; permissions: string[] }) => void
}

export function RoleEditor({
  role,
  isNew,
  isOpen,
  isSaving,
  onClose,
  onSave,
}: RoleEditorProps) {
  const t = useT()
  const { permissions: mine, has } = usePermissions()
  const catalogue = usePermissionCatalogue(isOpen && has(PERMISSIONS.roleManage))

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  // Re-seed whenever the drawer opens on a different role. Without the guard on
  // `isOpen` a re-render mid-edit would discard what the person has ticked.
  useEffect(() => {
    if (!isOpen) return
    setName(role?.name ?? '')
    setDescription(role?.description ?? '')
    setSelected(new Set(role?.permissions ?? []))
  }, [isOpen, role?.id, role?.name, role?.description, role?.permissions])

  const grouped = useMemo(() => {
    const rows = catalogue.data ?? []
    const byCategory = new Map<string, PermissionDescriptor[]>()
    for (const row of rows) {
      const list = byCategory.get(row.category) ?? []
      list.push(row)
      byCategory.set(row.category, list)
    }
    return [...byCategory.entries()]
  }, [catalogue.data])

  const held = useMemo(() => new Set(mine ?? []), [mine])
  const trimmedName = name.trim()

  const accessNotice = meetingAccessNotice(selected)

  function toggle(key: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      width="lg"
      title={isNew ? t('roles.editor.newRole') : t('roles.editor.editRole', { name: role?.name ?? t('roles.editor.role') })}
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-fg-subtle">
            {selected.size === 1
              ? t('roles.editor.permissionCount', { count: selected.size })
              : t('roles.editor.permissionCountPlural', { count: selected.size })}
          </span>
          <span className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!trimmedName || isSaving}
              onClick={() =>
                onSave({
                  name: trimmedName,
                  description: description.trim() || null,
                  permissions: [...selected],
                })
              }
            >
              {isNew ? t('roles.editor.createRole') : t('roles.editor.saveRole')}
            </Button>
          </span>
        </div>
      }
    >
      <div className="flex flex-col gap-5 px-4 py-4">
        <Input
          label={t('roles.editor.name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('roles.editor.namePlaceholder')}
          maxLength={60}
        />
        <Textarea
          label={t('roles.editor.description')}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder={t('roles.editor.descriptionPlaceholder')}
          rows={2}
          maxLength={280}
        />

        {role?.isSystem && !isNew && (
          <Alert tone="info" title={t('roles.editor.builtInTitle')}>
            {t('roles.editor.builtInBodyPrefix')}
            <code>{role.key}</code>
            {t('roles.editor.builtInBodySuffix')}
          </Alert>
        )}

        {accessNotice && (
          <Alert tone="warning" title={t(accessNotice.titleKey)}>
            {t(accessNotice.bodyKey)}
          </Alert>
        )}

        <QueryBoundary
          isLoading={catalogue.isPending}
          error={catalogue.error}
          onRetry={catalogue.refetch}
        >
          <div className="flex flex-col gap-6">
            {grouped.map(([category, rows]) => (
              <fieldset key={category} className="flex flex-col gap-3">
                <legend className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
                  {CATEGORY_KEY[category] ? t(CATEGORY_KEY[category]) : category}
                </legend>
                {rows.map((row) => {
                  // YOU CANNOT GRANT WHAT YOU DO NOT HOLD. The server enforces
                  // it; disabling the box here means the person is not invited
                  // to fill in a form that will 403 on save. An already-granted
                  // permission stays togglable so it can be REVOKED — taking
                  // something away is never escalation.
                  const alreadyOn = selected.has(row.key)
                  const grantable = held.has(row.key) || alreadyOn
                  return (
                    <label
                      key={row.key}
                      className="flex items-start gap-2.5 rounded-md p-1 hover:bg-surface-muted"
                    >
                      <Checkbox
                        checked={alreadyOn}
                        disabled={!grantable}
                        onChange={() => toggle(row.key)}
                      />
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5 font-medium text-fg">
                          {row.label}
                          {!grantable && (
                            <Lock
                              className="h-3 w-3 text-fg-subtle"
                              aria-label={t('roles.editor.youDoNotHold')}
                            />
                          )}
                        </span>
                        <span className="block text-sm text-fg-subtle">
                          {row.description}
                        </span>
                      </span>
                    </label>
                  )
                })}
              </fieldset>
            ))}
          </div>
        </QueryBoundary>
      </div>
    </Drawer>
  )
}
